import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { ClientUploadFile, ClientUploadReviewList } from '@shared/client-uploads';
import { apiRequest } from '@/lib/queryClient';

export type ClientReviewAction = 'approve' | 'reject' | 'restore' | 'purge';

export function useClientUploadReview(projectId: string, sessionId: string | undefined, bin: boolean, inViewport: boolean) {
  const client = useQueryClient();
  const base = `/api/client-uploads/projects/${encodeURIComponent(projectId)}`;
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    const changed = () => setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', changed);
    return () => document.removeEventListener('visibilitychange', changed);
  }, []);
  const prefix = ['/api/client-uploads/projects', projectId];
  const query = useInfiniteQuery({
    queryKey: [...prefix, sessionId || 'all', bin],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<ClientUploadReviewList> => {
      const params = new URLSearchParams();
      if (sessionId && !bin) params.set('sessionId', sessionId);
      if (bin) params.set('bin', 'true');
      if (pageParam) params.set('cursor', pageParam);
      return (await apiRequest('GET', `${base}${params.size ? `?${params}` : ''}`)).json();
    },
    getNextPageParam: page => page.nextCursor || undefined,
    enabled: !!projectId && inViewport && pageVisible,
    refetchInterval: inViewport && pageVisible ? 5000 : false,
    refetchIntervalInBackground: false,
    retry: false,
  });
  const review = useMutation({
    mutationFn: async ({ file, action, reason }: { file: ClientUploadFile; action: ClientReviewAction; reason?: string }) =>
      (await apiRequest('POST', `${base}/files/${file.id}/review`, { action, reviewVersion: file.reviewVersion, ...(reason ? { reason } : {}) })).json() as Promise<ClientUploadFile>,
    onSuccess: file => {
      client.setQueriesData<InfiniteData<ClientUploadReviewList>>({ queryKey: prefix }, old => old ? {
        ...old, pages: old.pages.map(page => ({ ...page, sessions: page.sessions.map(session => ({ ...session, files: session.files.map(existing => existing.id === file.id ? file : existing) })) })),
      } : old);
      // Review cards have no server-initialized editable content; reconcile bin/quota.
      void client.invalidateQueries({ queryKey: prefix });
    },
    onError: () => { void client.invalidateQueries({ queryKey: prefix }); },
  });
  const link = async (file: ClientUploadFile, download: boolean): Promise<{ url: string; mimeType: string }> => {
    if (!['approved', 'available'].includes(file.state)) throw new Error('Preview and download are available after internal approval.');
    return (await apiRequest('POST', `${base}/files/${file.id}/link`, { download })).json();
  };
  const approveAll = useMutation({
    mutationFn: async ({ sessionId: submissionId, files }: { sessionId: string; files: Pick<ClientUploadFile, 'id' | 'reviewVersion'>[] }): Promise<{ approved: number }> =>
      (await apiRequest('POST', `${base}/sessions/${encodeURIComponent(submissionId)}/approve-all`, { files })).json(),
    // Only the server can approve the batch; always refresh after success or failure.
    onSuccess: () => client.invalidateQueries({ queryKey: prefix }),
    onError: () => { void client.invalidateQueries({ queryKey: prefix }); },
  });
  const first = query.data?.pages[0];
  const sessions = query.data?.pages.flatMap(page => page.sessions) || [];
  const data: ClientUploadReviewList | undefined = first ? {
    ...first,
    sessions: sessions.filter((session, index) => sessions.findIndex(existing => existing.id === session.id) === index),
    nextCursor: query.data?.pages[query.data.pages.length - 1]?.nextCursor || null,
  } : undefined;
  return { query: { ...query, data }, review, approveAll, link };
}